import { type JwtPayload, jwtDecode } from "jwt-decode"

import { type Tokens } from "@/types/index"
import env from "@/utils/env"

const cognitoConfig = {
  UserPoolId: env.VITE_USER_POOL_ID,
  ClientId: env.VITE_APP_CLIENT_ID,
  RedirectUriSignIn: env.VITE_REDIRECT_SIGN_IN,
  AppWebDomain: env.VITE_COGNITO_DOMAIN,
}

/**
 * Get the dynamic redirect URI based on the current domain
 * This allows the app to work with multiple domains (e.g., rnp.io and akrochem.com)
 * by redirecting back to the same domain the user accessed
 */
const getRedirectUri = (path: string): string => {
  return `${window.location.origin}${path}`
}

const getSignInUrl =
  (): `https://${string}/login?response_type=code&client_id=${string}&redirect_uri=${string}&scope=openid email profile&state=${string}` => {
    const currentPath = encodeURIComponent(window.location.pathname)
    const redirectUri = getRedirectUri("/auth/callback")
    return `https://${cognitoConfig.AppWebDomain}/login?response_type=code&client_id=${cognitoConfig.ClientId}&redirect_uri=${redirectUri}&scope=openid email profile&state=${currentPath}`
  }

const getSignOutUrl = (): `https://${string}/logout?client_id=${string}&logout_uri=${string}` => {
  const logoutUri = getRedirectUri("/logout")
  return `https://${cognitoConfig.AppWebDomain}/logout?client_id=${cognitoConfig.ClientId}&logout_uri=${logoutUri}`
}

export const redirectToSignIn = (): void => {
  location.assign(getSignInUrl())
}

export const redirectToSignOut = (): void => {
  location.assign(getSignOutUrl())
}

export const getAuthCodeFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search)
  return params.get("code")
}

export const exchangeCodeForTokens = async (
  code: string
): Promise<{
  id_token: string
  access_token: string
  refresh_token: string
}> => {
  const redirectUri = getRedirectUri("/auth/callback")
  const response = await fetch(`https://${cognitoConfig.AppWebDomain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cognitoConfig.ClientId,
      code,
      redirect_uri: redirectUri,
    }),
  })

  if (!response.ok) {
    throw new Error(`Failed to exchange code for tokens: ${response.statusText}`)
  }

  const tokens = await response.json()
  return tokens
}

export const getIdTokenFromUrl = (): string | null => {
  const hash = window.location.hash.substring(1)
  const params = new URLSearchParams(hash)
  return params.get("id_token")
}

export const getTimeUntilExpiry = (token: string): number => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token)
    if (!exp) return 0
    return exp * 1000 - Date.now()
  } catch {
    return 0
  }
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token)

    if (!exp) {
      return true // Treat tokens with no expiration as expired
    }

    return Date.now() >= exp * 1000
  } catch (e) {
    console.error("Error decoding token:", e)
    return true // Treat errors in decoding as expired tokens
  }
}

export const getTokenExpiry = (tokenKey: keyof Tokens, token: string): Date | number => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token)
    if (decoded.exp) {
      return new Date(decoded.exp * 1000)
    }
  } catch (error) {
    console.warn(`Failed to decode ${tokenKey} for expiry:`, error)
  }

  return 3600 / 86400
}
